import { Form, notify } from '@/components';
import { useGetRoomsListQuery, useUpdateRoomsOrderMutation } from '@/redux';
import { array } from '@/utils';
import { Button, Col, Row } from 'antd';
import { useCallback, useMemo, useRef } from 'react';
import * as Yup from 'yup';
import { useDrag, useDrop, DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { HolderOutlined } from '@ant-design/icons';
import styles from './FormRoomOrder.module.scss';

interface Props {
  floorplanId: string;
  afterSubmit: () => void;
}

function useSubmit(props: Props) {
  const { floorplanId, afterSubmit } = props;
  const [save] = useUpdateRoomsOrderMutation();
  return useCallback(
    (values: FormValue) => {
      save({ rooms: values.rooms, floorplanId })
        .unwrap()
        .then(() => {
          notify.success.t('rooms.saved');
          afterSubmit();
        });
    },
    [afterSubmit, floorplanId, save],
  );
}

const SCHEMA = Yup.object().shape({
  rooms: Yup.array()
    .of(
      Yup.object().shape({
        id: Yup.string().required(),
        name: Yup.string().required(),
        index: Yup.number().required(),
      }),
    )
    .required(),
});

type FormValue = Yup.InferType<typeof SCHEMA>;

interface RoomOrderValue {
  id: string;
  name: string;
  index: number;
}

function useInitialValue(data: RoomOrderValue[]): FormValue {
  return useMemo(
    () =>
      ({
        rooms: data.map((room) => ({
          id: room.id,
          name: room.name,
          index: room.index,
        })),
      } as FormValue),
    [data],
  );
}

function useMoveRoom(
  rooms: RoomOrderValue[],
  setRooms: (rooms: RoomOrderValue[]) => void,
) {
  return useCallback(
    (dragIndex: number, hoverIndex: number) => {
      const newRooms = [...rooms];
      const [draggedRoom] = newRooms.splice(dragIndex, 1);
      newRooms.splice(hoverIndex, 0, draggedRoom);
      setRooms(newRooms.map((room, index) => ({ ...room, index })));
    },
    [rooms, setRooms],
  );
}

export function EditRoomOrderPanel(props: Props) {
  const { floorplanId } = props;
  const { data = array.empty<RoomOrderValue>(), isLoading } =
    useGetRoomsListQuery({
      floorplanId,
    });

  const submit = useSubmit(props);
  const initialValue = useInitialValue(data);

  if (isLoading) return <></>;

  return (
    <DndProvider backend={HTML5Backend}>
      <Form.Formik<FormValue>
        uid="rooms-order"
        i18n="rooms.details"
        validationSchema={SCHEMA}
        onSubmit={submit}
        initialValues={initialValue}
      >
        {({ values, setFieldValue }) => (
          <RoomList
            rooms={values.rooms}
            setRooms={(newRooms) => setFieldValue('rooms', newRooms)}
          />
        )}
      </Form.Formik>
    </DndProvider>
  );
}

interface RoomListProps {
  rooms: RoomOrderValue[];
  setRooms: (rooms: RoomOrderValue[]) => void;
}

function RoomList({ rooms, setRooms }: RoomListProps) {
  const moveRoom = useMoveRoom(rooms, setRooms);

  return (
    <>
      {rooms
        .slice()
        .sort((a, b) => a.index - b.index)
        .map((room, index) => (
          <RoomItem
            key={room.id}
            name={`rooms[${index}]`}
            index={index}
            moveRoom={moveRoom}
          />
        ))}
    </>
  );
}

interface RoomItemProps {
  name: string;
  index: number;
  moveRoom: (dragIndex: number, hoverIndex: number) => void;
}

function RoomItem(props: RoomItemProps) {
  const { name, index, moveRoom } = props;
  const ref = useRef<HTMLDivElement>(null);
  const dragRef = useRef<HTMLDivElement>(null);

  const [, drop] = useDrop({
    accept: 'ROOM',
    hover(item: { index: number }) {
      if (!ref.current) return;
      if (item.index !== index) {
        moveRoom(item.index, index);
        item.index = index;
      }
    },
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: 'ROOM',
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(dragRef);
  drop(preview(ref));

  return (
    <div ref={ref} style={{ opacity: isDragging ? 0.5 : 1 }}>
      <Row>
        <Col span={8} flex={1}>
          <Form.Input
            disabled
            className={styles.input}
            name={`${name}.name`}
            label={false}
          />
        </Col>
        <Col span={2}>
          <Button
            icon={<HolderOutlined />}
            style={{ cursor: 'move' }}
            ref={dragRef}
            type="text"
          />
        </Col>
      </Row>
    </div>
  );
}
